import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CashPaymentSummaryItem } from 'apis/oms';
import LOCALS from 'commons/locals';

type CashPaymentSummaryCardProps = {
  records?: CashPaymentSummaryItem[];
  currencySymbol: string;
  formatAmount: (amount: number | null | undefined) => string;
};

const CashPaymentSummaryCard: React.FC<CashPaymentSummaryCardProps> = ({
  records = [],
  currencySymbol,
  formatAmount,
}) => {
  const { t } = useTranslation();
  const { totalOrderCount, totalAmount } = useMemo(
    () => ({
      totalOrderCount: records.reduce(
        (total, record) => total + Number(record.orderCount || 0),
        0,
      ),
      totalAmount: records.reduce(
        (total, record) => total + Number(record.totalAmount || 0),
        0,
      ),
    }),
    [records],
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-6 pb-4 border-b">
        {t(LOCALS.daily_register_close_recycle_consignment_cash_payout)}
      </h2>

      {records.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">
                  {t(LOCALS.sHXnOEVSrC)}
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 border-b">
                  {t(LOCALS.order_count)}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">
                  {t(LOCALS.payment_voucher)}
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 border-b">
                  {t(LOCALS.total_amount)}
                </th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr
                  key={record.submissionId}
                  className="border-b border-gray-100"
                >
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {record.submissionId}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                    {record.orderCount}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {record.financialPaymentVoucher || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                    {currencySymbol} {formatAmount(record.totalAmount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-6 pt-4 border-t-2 border-gray-200">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between py-3 px-4 rounded-lg border bg-gray-50 border-gray-200">
                <span className="text-lg font-semibold text-gray-900">
                  {t(LOCALS.order_count)}
                </span>
                <span className="text-xl font-bold text-gray-700">
                  {totalOrderCount}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 px-4 rounded-lg border bg-amber-50 border-amber-200">
                <span className="text-lg font-semibold text-gray-900">
                  {t(
                    LOCALS.daily_register_close_recycle_consignment_cash_payout_total,
                  )}
                </span>
                <span className="text-xl font-bold text-amber-700">
                  {currencySymbol} {formatAmount(totalAmount)}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          {t(
            LOCALS.daily_register_close_no_recycle_consignment_cash_payout_today,
          )}
        </div>
      )}
    </div>
  );
};

export default CashPaymentSummaryCard;
